// ---------------------------------------------------------------------------
// Design System Options — 6-axis schema (Phase 365-01)
//
// Single source of truth for the /studio/design-system customizer state.
// All six axes map 1:1 to keys exported from `./shadcn-presets`, which is
// the shared oklch preset source used by BlockGridTestPage and the design
// system preview.
//
// Previous 10-field schema (style, baseColor, theme, chartColor, headingFont,
// bodyFont, iconLibrary, radius, menuStyle, menuAccent) has been collapsed to
// these 6 axes per Phase 365 D-04/D-05. Deferred axes (menuStyle, menuAccent,
// iconLibrary, separate heading/body font) are tracked in 365-CONTEXT.md.
// ---------------------------------------------------------------------------

import {
  BASE_COLORS,
  STYLES,
  RADII_LIST,
  THEMES,
  CHART_PALETTES,
  FONTS,
  type Style,
} from './shadcn-presets'
import {
  axesToThemeV2,
  CANONICAL_THEME,
  type DesignSystemAxes,
} from '@cogo/design-presets'

// ---------------------------------------------------------------------------
// Axis key types — derived from shadcn-presets so adding a preset there
// automatically widens the acceptable state values.
// ---------------------------------------------------------------------------

export type StyleKey = Style
export type BaseColorKey = (typeof BASE_COLORS)[number]['name']
export type ThemeKey = keyof typeof THEMES
export type ChartKey = keyof typeof CHART_PALETTES
export type RadiusKey = (typeof RADII_LIST)[number]['name']
export type FontKey = keyof typeof FONTS

// ---------------------------------------------------------------------------
// 6-axis DesignSystemState
// ---------------------------------------------------------------------------

export interface DesignSystemState {
  /** shadcn style preset (lyra, vega, nova, maia, mira, luma). */
  style: StyleKey
  /** Neutral palette (neutral, stone, zinc, mauve, olive, mist, taupe). */
  baseColor: BaseColorKey
  /** Theme overlay — primary hue preset. */
  theme: ThemeKey
  /** Chart palette — only --chart-1..5 slots are pulled from this preset. */
  chart: ChartKey
  /** Radius preset name (default, none, small, medium, large). */
  radius: RadiusKey
  /** Body font preset (geist, inter, …). */
  font: FontKey
}

// ---------------------------------------------------------------------------
// Defaults — mirror BlockGridTestPage initial state (lines 137-142):
//   style='lyra', baseColor=BASE_COLORS[0]='neutral',
//   theme=Object.keys(THEMES)[0], chart=Object.keys(CHART_PALETTES)[0],
//   radius=RADII_LIST[0]='default' (0.625rem), font='geist'
// ---------------------------------------------------------------------------

const FIRST_THEME = Object.keys(THEMES)[0] as ThemeKey
const FIRST_CHART = Object.keys(CHART_PALETTES)[0] as ChartKey

/**
 * The style the platform's canonical theme is built from.
 *
 * Derived, not written: `themes/default-theme.json` is the baseline the system
 * declares, and its per-component atoms are what identify the style — writing
 * `'vega'` here would be correct today and silently stale the day that file
 * changes. Falls back to the first style only if no style reproduces the
 * canonical atoms, which would itself be a drift worth seeing.
 */
const CANONICAL_STYLE: StyleKey = (() => {
  const declared = (CANONICAL_THEME as { atoms?: Record<string, Record<string, unknown>> }).atoms
  const card = declared?.card
  if (card) {
    // Asked of the producer rather than of its lookup table: run each style
    // through the same function the preview paints with and keep the one whose
    // per-component defaults reproduce what the canonical theme declares. This
    // needs no export from the presets package and cannot drift from the paint
    // path, because it IS the paint path.
    const match = STYLES.find((style) => {
      const painted = axesToThemeV2({
        style,
        baseColor: BASE_COLORS[0].name,
        theme: Object.keys(THEMES)[0],
        chart: Object.keys(CHART_PALETTES)[0],
        radius: RADII_LIST[0].name,
        font: 'geist',
      } as DesignSystemAxes).atoms?.card as Record<string, unknown> | undefined
      return painted != null && Object.entries(painted).every(([k, v]) => card[k] === v)
    })
    if (match) return match as StyleKey
  }
  return STYLES[0] as StyleKey
})()

export const DEFAULT_DESIGN_SYSTEM_STATE: DesignSystemState = {
  style: CANONICAL_STYLE,
  baseColor: BASE_COLORS[0].name as BaseColorKey,
  theme: FIRST_THEME,
  chart: FIRST_CHART,
  radius: RADII_LIST[0].name as RadiusKey,
  font: 'geist',
}

/**
 * Backward-compatible alias. The legacy identifier `DEFAULT_DESIGN_SYSTEM`
 * is still referenced by `store/theme-slice.ts` and assorted tests; keeping
 * the alias avoids a sweeping rename in this plan.
 */
export const DEFAULT_DESIGN_SYSTEM: DesignSystemState = DEFAULT_DESIGN_SYSTEM_STATE

// ---------------------------------------------------------------------------
// Labeled option lists — used by the 6-axis customizer UI.
// ---------------------------------------------------------------------------

export interface LabeledOption {
  value: string
  label: string
}

/**
 * The 6 axis keys in canonical order. `satisfies` keeps this literal in
 * lockstep with `DesignSystemState` — adding/removing an axis there makes
 * this line a compile error instead of a silently stale list.
 */
export const AXIS_KEYS = [
  'style',
  'baseColor',
  'theme',
  'chart',
  'radius',
  'font',
] as const satisfies readonly (keyof DesignSystemState)[]

function titleCase(s: string): string {
  return s
    .split(/[-_ ]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

export const STYLE_OPTIONS: LabeledOption[] = STYLES.map((name) => ({
  value: name,
  label: titleCase(name),
}))

export const BASE_COLOR_OPTIONS: LabeledOption[] = BASE_COLORS.map((b) => ({
  value: b.name,
  label: titleCase(b.name),
}))

export const THEME_OPTIONS: LabeledOption[] = Object.keys(THEMES).map((name) => ({
  value: name,
  label: titleCase(name),
}))

export const CHART_OPTIONS: LabeledOption[] = Object.keys(CHART_PALETTES).map((name) => ({
  value: name,
  label: titleCase(name),
}))

export const RADIUS_OPTIONS: LabeledOption[] = RADII_LIST.map((r) => ({
  value: r.name,
  label: r.label,
}))

export const FONT_OPTIONS: LabeledOption[] = Object.keys(FONTS).map((name) => ({
  value: name,
  label: titleCase(name),
}))
